import { InteractionType } from '@azure/msal-browser';

import { getMsalInteractionType } from './msalInteractionType.js';

interface AuthRedirectLocation {
  pathname: string;
  search: string;
  hash: string;
}

interface AuthRedirectState {
  version: 1;
  returnTo: string;
  /** The full return path was too long for state and is in sessionStorage. */
  returnToStored?: true;
}

// Entra ID rejects authorize requests whose URL is too long, and deeplinks
// such as /debug#trajectory=<gzip> can be tens of kilobytes. Longer return
// paths are kept in sessionStorage, which survives the same-tab redirect.
const MAX_STATE_RETURN_PATH_LENGTH = 2048;
const STORED_RETURN_PATH_KEY = 'ai-studio.auth.returnPath';

export function getAuthReturnPath(location: AuthRedirectLocation): string {
  const hashParams = new URLSearchParams(location.hash.slice(1));
  if (
    (hashParams.has('code') || hashParams.has('error')) &&
    getMsalInteractionType({ hash: location.hash, search: '' }) ===
      InteractionType.Redirect
  ) {
    return `${location.pathname}${location.search}`;
  }

  const queryParams = new URLSearchParams(location.search.slice(1));
  if (
    (queryParams.has('code') || queryParams.has('error')) &&
    getMsalInteractionType({ hash: '', search: location.search }) ===
      InteractionType.Redirect
  ) {
    for (const key of [
      'code',
      'state',
      'session_state',
      'client_info',
      'error',
      'error_description',
      'error_uri',
    ]) {
      queryParams.delete(key);
    }
    const search = queryParams.toString();
    return `${location.pathname}${search ? `?${search}` : ''}${location.hash}`;
  }

  return `${location.pathname}${location.search}${location.hash}`;
}

export function createAuthRedirectState(
  location: AuthRedirectLocation,
): string {
  const returnTo = getAuthReturnPath(location);
  if (returnTo.length > MAX_STATE_RETURN_PATH_LENGTH) {
    const pathAndQuery = `${location.pathname}${location.search}`;
    const shortReturnTo =
      pathAndQuery.length <= MAX_STATE_RETURN_PATH_LENGTH
        ? pathAndQuery
        : location.pathname;
    try {
      sessionStorage.setItem(STORED_RETURN_PATH_KEY, returnTo);
      return JSON.stringify({
        version: 1,
        returnTo: shortReturnTo,
        returnToStored: true,
      } satisfies AuthRedirectState);
    } catch {
      return JSON.stringify({
        version: 1,
        returnTo: shortReturnTo,
      } satisfies AuthRedirectState);
    }
  }
  return JSON.stringify({
    version: 1,
    returnTo,
  } satisfies AuthRedirectState);
}

function toSameOriginReturnPath(
  returnTo: unknown,
  origin: string,
): string | null {
  if (
    typeof returnTo !== 'string' ||
    !returnTo.startsWith('/') ||
    returnTo.startsWith('//') ||
    returnTo.startsWith('/\\')
  ) {
    return null;
  }
  const returnUrl = new URL(returnTo, origin);
  if (returnUrl.origin !== origin) return null;
  return getAuthReturnPath(returnUrl);
}

function takeStoredReturnPath(): string | null {
  try {
    const stored = sessionStorage.getItem(STORED_RETURN_PATH_KEY);
    sessionStorage.removeItem(STORED_RETURN_PATH_KEY);
    return stored;
  } catch {
    return null;
  }
}

export function parseAuthRedirectState(
  state: string | undefined,
  origin: string,
): string | null {
  if (!state) return null;

  try {
    const parsed = JSON.parse(state) as Partial<AuthRedirectState>;
    if (parsed.version !== 1) return null;
    const returnTo = toSameOriginReturnPath(parsed.returnTo, origin);
    if (returnTo === null) return null;
    if (parsed.returnToStored) {
      const stored = toSameOriginReturnPath(takeStoredReturnPath(), origin);
      // Only trust a stored path that extends the path carried in state.
      if (stored?.startsWith(returnTo)) return stored;
    }
    return returnTo;
  } catch {
    return null;
  }
}
